<script setup lang="ts">
/** Letter of Indemnity — for named Company Secretary engaged by CSP */
import { ref, inject, watch, type Ref } from 'vue';
import CddVarField from '../incorporation-pre/cdd/CddVarField.vue';
import DocScope from '../incorporation-pre/cdd/DocScope.vue';
import SgPostSigners from './SgPostSigners.vue';
import { SG_POST_SIGNER_KEYS } from './sgPostSignersContext';
import './allotDoc.css';
import type { SgPostChildLocal } from './sgPostChildLocal';

let seq = 1;
const directors = ref<number[]>([1]);
function add() { directors.value.push(++seq); }
function del(id: number) { if (directors.value.length > 1) directors.value = directors.value.filter((x) => x !== id); }

const childLocal = inject<Ref<SgPostChildLocal> | null>('sgIncPostChildLocal', null);
watch(
  () => childLocal?.value?.indemnityIds,
  (ids) => {
    if (!Array.isArray(ids) || !ids.length) return;
    directors.value = [...ids];
    seq = Math.max(seq, ...ids);
  },
  { immediate: true },
);
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title" style="text-align:center; font-size:16px">LETTER OF INDEMNITY</div>
    </div>

    <p class="doc-p">Date: <CddVarField field-key="letter_date" label="Letter Date" /></p>
    <p class="doc-p">
      To:<br />
      THE NAMED COMPANY SECRETARY ENGAGED BY<br />
      <CddVarField field-key="csp_name" label="CSP Name" /><br />
      <CddVarField field-key="csp_address_line1" label="CSP Address Line 1" /><br />
      <CddVarField field-key="csp_address_line2" label="CSP Address Line 2" />
    </p>
    <p class="doc-p">Dear Sir/Madam,</p>
    <p class="doc-lbl-title"><CddVarField field-key="company_name" label="Company Name" /></p>
    <p class="doc-lbl-title">APPOINTMENT OF COMPANY SECRETARY</p>

    <p class="doc-p">
      In consideration of your so acting as the Secretary of
      <CddVarField field-key="company_name" label="Company Name" />
      (the "Company") and undertaking the duties of the said office at our request, we furnish you with this
      Letter of Indemnity.
    </p>
    <p class="doc-p">
      We, the undersigned, hereby personally undertake that we will, at all times, well and sufficiently
      indemnify you and keep you indemnified against all claims and demands for which you may, in the course
      of the due performance of your duties as the Secretary aforesaid, render yourself legally liable, and
      against all actions, suits, proceedings, claims, demands, costs and expenses whatsoever which may be
      taken or made against you or incurred or become payable by you in the course of the execution of your
      duties as the Secretary.
    </p>
    <p class="doc-p">
      We further undertake to pay or reimburse you (as the case may require) so much of the expenses properly
      incurred by you in the due performance of your duties as the Secretary as aforesaid, and so much of
      your remuneration for so acting as you are unable to recover from the monies coming into your hands as
      the Secretary.
    </p>
    <p class="doc-p">
      We hereby confirm that this undertaking and indemnity on your part shall be continuing and irrevocable
      and shall remain in force notwithstanding your subsequent discharge, until the Company is liquidated
      and dissolved.
    </p>

    <p class="doc-lbl-title">CONFIRMED BY / WITNESSED BY</p>
    <div v-for="id in directors" :key="id" class="indem-row">
      <DocScope :scope="`indem_${id}/`">
        <table class="cdd-tbl">
          <thead>
            <tr><th>Confirmed By</th><th>Witnessed By</th><th style="width:36px" /></tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Signature: _____________________________<br />
                Name: <CddVarField field-key="d_name" label="Director Name" /><br />
                Identification No.: <CddVarField field-key="d_identification_no" label="Director ID" /><br />
                Date: <CddVarField field-key="d_signature_date" label="Signature Date" />
              </td>
              <td>
                Signature: _____________________________<br />
                Name: <CddVarField field-key="d_witness_name" label="Witness Name" /><br />
                Identification No.: <CddVarField field-key="d_witness_identification_no" label="Witness ID" /><br />
                Date: <CddVarField field-key="d_witness_date" label="Witness Date" />
              </td>
              <td>
                <button type="button" class="cdd-rowbtn" :disabled="directors.length <= 1" @click="del(id)">×</button>
              </td>
            </tr>
          </tbody>
        </table>
      </DocScope>
    </div>
    <button type="button" class="cdd-addrow" @click="add">+ Add director / witness block</button>
    <!-- 이사 1인 — 자동 시드 없음, Add Signers 로 직접 지정 -->
    <SgPostSigners :var-key="SG_POST_SIGNER_KEYS.indemnity" board-label="DIRECTOR (COMPANY REPRESENTATIVE)" />
  </div>
</template>

<style scoped>
.indem-row { margin-bottom: 12px; }
</style>
